// src/components/admin/WorkspacesTab.tsx
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import {
  useAdminWorkspaces,
  useAdminWorkspace,
  useCreateWorkspace,
  useUpdateWorkspace,
  useDeleteWorkspace,
  useAssignTeamToWorkspace,
  useRemoveTeamFromWorkspace,
  useAdminTeams,
} from "../../hooks/useAdminData";
import { useUserRole } from "../../hooks/useUserRole";
import CreateEntityDialog, { CreateEntityDialogProps } from "./CreateEntityDialog";
import AddTeamPopover from "./AddTeamPopover";
import AdminSearchHeader from "./AdminSearchHeader";
import AdminSnackbar, { AdminSnackState } from "./AdminSnackbar";

const GridItem: any = Grid;

type CreateWorkspaceDialogProps = Omit<CreateEntityDialogProps, "title" | "nameLabel">;

const CreateWorkspaceDialog: React.FC<CreateWorkspaceDialogProps> = (props) => (
  <CreateEntityDialog
    {...props}
    title="Create Workspace"
    nameLabel="Workspace Name"
  />
);

const WorkspacesTab: React.FC = () => {
  const { isSuper, isAdmin } = useUserRole();
  const { data: workspaces, isLoading, error } = useAdminWorkspaces();
  const { data: allTeams } = useAdminTeams();
  const createWorkspace = useCreateWorkspace();
  const updateWorkspace = useUpdateWorkspace();
  const deleteWorkspace = useDeleteWorkspace();
  const assignTeam = useAssignTeamToWorkspace();
  const removeTeam = useRemoveTeamFromWorkspace();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);
  const [snack, setSnack] = useState<AdminSnackState | null>(null);
  const [teamPopoverAnchor, setTeamPopoverAnchor] = useState<HTMLElement | null>(null);
  const [teamPopoverWsId, setTeamPopoverWsId] = useState<number>(0);
  const [editingWs, setEditingWs] = useState<{ id: number; name: string; description: string } | null>(null);

  const { data: selectedWsData } = useAdminWorkspace(selectedWorkspaceId);

  const filtered = useMemo(() => {
    if (!workspaces) return [];
    const q = search.toLowerCase();
    return workspaces.filter(
      (ws) => ws.name.toLowerCase().includes(q) || ws.description?.toLowerCase().includes(q)
    );
  }, [workspaces, search]);

  const handleCreate = async (name: string, description: string) => {
    try {
      await createWorkspace.mutateAsync({ name, description });
      setSnack({ msg: "Workspace created", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to create workspace", severity: "error" });
    }
  };

  const handleUpdate = async () => {
    if (!editingWs) return;
    try {
      await updateWorkspace.mutateAsync({
        workspaceId: editingWs.id,
        name: editingWs.name,
        description: editingWs.description,
      });
      setEditingWs(null);
      setSnack({ msg: "Workspace updated", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to update workspace", severity: "error" });
    }
  };

  const handleDelete = async (wsId: number, wsName: string) => {
    if (
      !globalThis.confirm(
        `Delete workspace "${wsName}"? This will remove all team assignments and resources.`
      )
    ) {
      return;
    }
    try {
      await deleteWorkspace.mutateAsync(wsId);
      if (selectedWorkspaceId === wsId) setSelectedWorkspaceId(null);
      setSnack({ msg: "Workspace deleted", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to delete workspace", severity: "error" });
    }
  };

  const handleAssignTeam = async (teamId: number) => {
    try {
      await assignTeam.mutateAsync({ workspaceId: teamPopoverWsId, teamId });
      setSnack({ msg: "Team assigned", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to assign team", severity: "error" });
    }
  };

  const handleRemoveTeam = async (workspaceId: number, teamId: number) => {
    try {
      await removeTeam.mutateAsync({ workspaceId, teamId });
      setSnack({ msg: "Team removed from workspace", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to remove team", severity: "error" });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} sx={{ color: "#EF2D3E" }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load workspaces. Check your permissions.</Alert>;
  }

  return (
    <Box>
      <AdminSearchHeader
        title="Workspaces"
        count={filtered.length}
        searchPlaceholder="Search workspaces..."
        search={search}
        onSearchChange={setSearch}
        showCreateButton={isAdmin}
        createLabel="New Workspace"
        onCreateClick={() => setCreateOpen(true)}
      />

      <Grid container spacing={2}>
        {filtered.map((ws) => {
          return (
            <GridItem item xs={12} sm={6} md={4} key={ws.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  cursor: "pointer",
                  borderColor: selectedWorkspaceId === ws.id ? "#EF2D3E" : undefined,
                  borderWidth: selectedWorkspaceId === ws.id ? 2 : 1,
                  transition: "border-color 0.2s",
                  "&:hover": { borderColor: "#EF2D3E" },
                }}
                onClick={() => setSelectedWorkspaceId(selectedWorkspaceId === ws.id ? null : ws.id)}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    {editingWs?.id === ws.id ? (
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          size="small"
                          value={editingWs.name}
                          onChange={(e) => setEditingWs({ ...editingWs, name: e.target.value })}
                          onBlur={handleUpdate}
                          onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
                          onClick={(e) => e.stopPropagation()}
                          fullWidth
                          autoFocus
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          size="small"
                          value={editingWs.description}
                          onChange={(e) => setEditingWs({ ...editingWs, description: e.target.value })}
                          onBlur={handleUpdate}
                          onClick={(e) => e.stopPropagation()}
                          fullWidth
                          multiline
                        />
                      </Box>
                    ) : (
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{ws.name}</Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: "#6B7280", mt: 0.5 }}>
                          {ws.description || "No description"}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mt: 1.5 }}>
                    <Chip
                      icon={<GroupsIcon sx={{ fontSize: 14 }} />}
                      label={`${ws.team_count} teams`}
                      size="small"
                      sx={{ fontSize: "0.75rem", bgcolor: "#F3F4F6" }}
                    />
                    <Chip
                      icon={<FolderIcon sx={{ fontSize: 14 }} />}
                      label={`${ws.resource_count} resources`}
                      size="small"
                      sx={{ fontSize: "0.75rem", bgcolor: "#F3F4F6" }}
                    />
                  </Box>

                  {/* Teams in workspace */}
                  {(ws.teams || []).length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF", mb: 0.5, fontWeight: 600 }}>
                        ASSIGNED TEAMS
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(ws.teams || []).map((t) => (
                          <Chip
                            key={t.id}
                            label={t.name}
                            size="small"
                            onDelete={
                              isSuper
                                ? (e) => {
                                    e.stopPropagation();
                                    handleRemoveTeam(ws.id, t.id);
                                  }
                                : undefined
                            }
                            deleteIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              bgcolor: "#EFF6FF",
                              color: "#1D4ED8",
                              fontSize: "0.7rem",
                              height: 22,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>

                {isAdmin && (
                  <CardActions sx={{ px: 2, pt: 0, pb: 1.5, justifyContent: "flex-end" }}>
                    <Tooltip title="Assign team">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTeamPopoverWsId(ws.id);
                          setTeamPopoverAnchor(e.currentTarget);
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit workspace">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingWs({
                            id: ws.id,
                            name: ws.name,
                            description: ws.description || "",
                          });
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {isSuper && (
                      <Tooltip title="Delete workspace">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(ws.id, ws.name);
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </CardActions>
                )}
              </Card>
            </GridItem>
          );
        })}
        {filtered.length === 0 && (
          <GridItem item xs={12}>
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography color="text.secondary">No workspaces found</Typography>
            </Box>
          </GridItem>
        )}
      </Grid>

      {/* Workspace detail panel */}
      {selectedWsData && (
        <Box sx={{ mt: 3, p: 2, border: "1px solid #E5E7EB", borderRadius: 2, bgcolor: "#FAFBFC" }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Workspace Details: {selectedWsData.name}
          </Typography>
          {selectedWsData.resources && selectedWsData.resources.length > 0 ? (
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.5, color: "#6B7280" }}>
                Resources
              </Typography>
              {selectedWsData.resources.map((r) => (
                <Chip
                  key={r.id}
                  label={`${r.resource_type}: ${r.resource_name || r.resource_id}`}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5, fontSize: "0.75rem" }}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#9CA3AF" }}>
              No resources assigned to this workspace yet.
            </Typography>
          )}
        </Box>
      )}

      <CreateWorkspaceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        isLoading={createWorkspace.isPending}
      />

      <AddTeamPopover
        anchorEl={teamPopoverAnchor}
        onClose={() => setTeamPopoverAnchor(null)}
        teams={allTeams || []}
        assignedTeamIds={
          workspaces?.find((ws) => ws.id === teamPopoverWsId)?.teams?.map((t) => t.id) || []
        }
        onSelect={handleAssignTeam}
      />

      <AdminSnackbar snack={snack} onClose={() => setSnack(null)} />
    </Box>
  );
};

export default WorkspacesTab;
